import React, { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { ChevronRight, Loader, Package, ArrowBigLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import DetailedOrderView from './DetailedOrderView.jsx';
import { get_tracking_data_from_cloud } from './api/FirestoreUtils';
import { trackShipments } from './cart/Utils.js'; // Adjust the import path as needed
import FooterSection from './landing/FooterSection.jsx';

const auth = getAuth();
const db = getFirestore();
const gold = '#DBAE6E';

const OrdersScreen = ({ setScreen }) => {
  const [orders, setOrders] = useState([]);
  const [trackingData, setTrackingData] = useState({}); // Store tracking details by orderId
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingTracking, setLoadingTracking] = useState({}); // Track loading state for each order's tracking
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) {
      setLoadingOrders(false);
      setErrorMessage('Sign in to view orders');
      return;
    }

    const fetchOrders = async () => {
      try {
        const userOrdersRef = collection(db, 'Orders', user.email, 'UserOrders');
        const querySnapshot = await getDocs(userOrdersRef);

        const orderList = querySnapshot.docs.map(doc => ({
          orderId: doc.id,
          ...doc.data(),
        }));

        // Sort orders by createdAt (newest first)
        orderList.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });

        setOrders(orderList);
        setLoadingOrders(false);

        // Initialize loading state for tracking details
        const initialLoadingTracking = {};
        orderList.forEach(order => {
          initialLoadingTracking[order.orderId] = true;
        });
        setLoadingTracking(initialLoadingTracking);

        // Fetch tracking details for each order in parallel
        const trackingPromises = orderList.map(async (order) => {
          try {
            const trackingResponse = await get_tracking_data_from_cloud(user.email, order.orderId);
            return {
              orderId: order.orderId,
              trackingDetails: trackingResponse.success && trackingResponse.data ? trackingResponse.data : [],
            };
          } catch (error) {
            console.error(`Error fetching tracking data for Order ${order.orderId}:`, error);
            return { orderId: order.orderId, trackingDetails: [] };
          }
        });

        const trackingResults = await Promise.all(trackingPromises);

        // Update tracking data and loading states
        const newTrackingData = {};
        const newLoadingTracking = { ...initialLoadingTracking };
        trackingResults.forEach(({ orderId, trackingDetails }) => {
          newTrackingData[orderId] = trackingDetails;
          newLoadingTracking[orderId] = trackingDetails.length > 0; // Keep loading if tracking details exist
        });

        setTrackingData(newTrackingData);
        setLoadingTracking(newLoadingTracking);

        // Extract and console log all tracking numbers
        const trackingNumbers = trackingResults
          .flatMap(({ trackingDetails }) => trackingDetails?.map(t => t.trackingNumber) || [])
          .filter(trackingNumber => trackingNumber && typeof trackingNumber === 'string' && trackingNumber.trim() !== '');

        if (trackingNumbers.length > 0) {
          try {
            const trackingResponse = await trackShipments(trackingNumbers);
            // Update trackingData with trackShipments response
            const updatedTrackingData = { ...newTrackingData };
            trackingResponse.data.forEach((tracking) => {
              const order = orderList.find(o => 
                updatedTrackingData[o.orderId]?.some(t => t.trackingNumber === tracking.trackingNumber)
              );
              if (order) {
                updatedTrackingData[order.orderId] = updatedTrackingData[order.orderId].map(t => 
                  t.trackingNumber === tracking.trackingNumber ? { ...t, ...tracking } : t
                );
              }
            });
            setTrackingData(updatedTrackingData);
            // Set loading to false for all orders after tracking response
            const finalLoadingTracking = {};
            orderList.forEach(order => {
              finalLoadingTracking[order.orderId] = false;
            });
            setLoadingTracking(finalLoadingTracking);
          } catch (error) {
            console.error('Error calling trackShipments:', error);
            // Set loading to false even on error to avoid infinite loading
            const finalLoadingTracking = {};
            orderList.forEach(order => {
              finalLoadingTracking[order.orderId] = false;
            });
            setLoadingTracking(finalLoadingTracking);
          }
        } else {
          // Set loading to false if no tracking numbers
          const finalLoadingTracking = {};
          orderList.forEach(order => {
            finalLoadingTracking[order.orderId] = false;
          });
          setLoadingTracking(finalLoadingTracking);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
        setErrorMessage('Failed to load orders');
        setLoadingOrders(false);
      }
    };

    fetchOrders();
  }, [user]);

  const getStatusStyle = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'delivered':
        return 'bg-green-700 text-black dark:text-white';
      case 'shipped':
        return 'bg-blue-700 text-black dark:text-white';
      case 'placed':
        return 'bg-yellow-500 text-black';
      case 'cancelled':
        return 'bg-red-700 text-black dark:text-white';
      case 'dispatched':
        return 'bg-yellow-700 text-black dark:text-white';
      case 'exchanging':
        return 'bg-violet-700 text-black dark:text-white';
      case 'returning':
        return 'bg-yellow-700 text-black dark:text-white';
      case 'returned':
        return 'bg-orange-700 text-black dark:text-white';
      case 'exchange':
        return 'bg-purple-700 text-black dark:text-white';
      case 'exchanged':
        return 'bg-pink-700 text-black dark:text-white';
      default:
        return 'bg-gray-600 text-black dark:text-white';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  if (selectedOrder) {
    return (
      <DetailedOrderView
        order={{
          ...selectedOrder,
          trackingDetails: trackingData[selectedOrder.orderId] || [],
        }}
        setSelectedOrder={setSelectedOrder}
      />
    );
  }

  return (
    <div className="min-h-screen bg-primary dark:bg-primary-dark py-16 px-4 text-black dark:text-white font-sans overflow-x-hidden">
      <div className="max-w-[100vw] mx-auto md:max-w-6xl w-full">
        <h1
          className="text-3xl md:text-5xl font-extrabold uppercase tracking-wide text-center mb-8 md:mb-12"
          style={{ color: gold }}
        >
          Your Orders
        </h1>



        {errorMessage && (
          <div className="mb-6 p-4 bg-red-800 border-l-4 border-red-500 text-white dark:text-white rounded shadow-md w-full max-w-[calc(100vw-2rem)] mx-auto">
            {errorMessage}
          </div>
        )}

        {loadingOrders ? (
          <div className="flex justify-center items-center py-24">
            <Loader className="animate-spin text-black dark:text-white" size={48} />
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white dark:bg-neutral-800 border border-neutral-700 rounded-xl shadow-lg p-8 text-center w-full max-w-[calc(100vw-2rem)] mx-auto">
            <Package size={48} className="mx-auto mb-4 text-gray-500" />
            <p className="text-gray-500 text-base md:text-lg">No orders yet</p>
          </div>
        ) : (
          <motion.div
            className="grid gap-6 w-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
          >
            {orders.map((order) => (
              <motion.div
                key={order.orderId}
                className="bg-primary dark:bg-primary-dark rounded-xl shadow-xl p-4 md:p-6 border border-[#DBAE6E]/40 hover:shadow-gold transition-all cursor-pointer w-full max-w-[calc(100vw-2rem)] mx-auto"
                onClick={() => setSelectedOrder(order)}
                whileHover={{ scale: 1.015 }}
                transition={{ type: 'spring', stiffness: 120 }}
              >
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-lg text-secondary tracking-widest truncate">
                      #{order.orderId}
                    </p>
                    <p className="text-base md:text-lg font-bold text-black dark:text-white mt-1 truncate">
                      {formatDate(order.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center flex-shrink-0">
                    {(!trackingData[order.orderId] || trackingData[order.orderId].length === 0) && (
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm whitespace-nowrap ${getStatusStyle(
                          order.orderStatus
                        )}`}
                      >
                        {order.orderStatus}
                      </span>
                    )}
                    <ChevronRight className="ml-2 md:ml-4 text-black dark:text-white" size={24} />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 text-sm text-black dark:text-white">
                  <div className="space-y-2">
                    <p className="text-xs text-gray-400 uppercase flex items-center">
                      <Package size={16} className="mr-2 text-gold" /> Items
                    </p>
                    <p className="font-medium break-words">
                      {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs text-gray-400 uppercase flex items-center">
                      <Package size={16} className="mr-2 text-gold" /> Tracking Details
                    </p>
                    {loadingTracking[order.orderId] ? (
                      <div className="flex justify-center items-center py-4">
                        <Loader className="animate-spin text-black dark:text-white" size={24} />
                      </div>
                    ) : trackingData[order.orderId]?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-black dark:text-white">
                          <thead className="text-xs uppercase bg-primary dark:bg-primary-dark">
                            <tr>
                              <th scope="col" className="px-4 py-2">Content</th>
                              <th scope="col" className="px-4 py-2">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trackingData[order.orderId].map((tracking, index) => (
                              <tr
                                key={index}
                                className="border-b border-neutral-700 "
                              >
                                <td className="px-4 py-2 font-medium">
                                  {tracking.content?.content || tracking.packages?.[0]?.content || 'N/A'}
                                </td>
                                <td className="px-4 py-2">
                                  {tracking.status ? (
                                    <span
                                      className="px-3 py-1 rounded-full text-xs font-semibold shadow-sm whitespace-nowrap"
                                      style={{
                                        backgroundColor: tracking.statusColor || '#gray-600',
                                        color: '#fff',
                                      }}
                                    >
                                      {tracking.status}
                                    </span>
                                  ) : (
                                    'N/A'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-400">No tracking details available</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <p className="font-bold text-lg md:text-xl text-[#DBAE6E]">
                      ₹{order.totalPrice}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
      <FooterSection/>
    </div>
  );
};

export default OrdersScreen;